/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */
import { Injectable } from '@angular/core';
import { Editor } from 'ckeditor5';
import { MEDIA, UndoRedoService } from './undo-redo.service';
import { EditorUtil } from '../editor.util';
import { CKEditorService } from './ckeditor.service';
import { ImageCarouselService } from './image-carousel.service';

/**
 * Service which will handle registered cke events.
 */
@Injectable({
  providedIn: 'root',
})
export class CKEEventService {

  /**
   * Default Constructor
   * @param undoRedoService Service which handle api communication for page editor.
   * @param ckEditorService Service which have all service getting with cke plugin.
   * @param imageCarouselService Service which handle image carousel operations.
   */
  constructor(
    private undoRedoService : UndoRedoService,
    private ckEditorService : CKEditorService,
    private imageCarouselService: ImageCarouselService,
  ) {

  }

  /**
   * Configure global event for the editor.
   *
   * @param editor editor data.
   * @param unit page data.
   * @param editable check if the page is editable.
   */
  registerEvents(editor: Editor, unit, editable) {
    CKEEventService.handleCopyAndCutEvent(editor);
    CKEEventService.handlePasteEvent(editor);
    CKEEventService.configureEditorDragEvent(editor);
    if (editable) {
      this.configureKeydownEvent(editor, unit);
      this.differChanges(editor, unit);
    }
    this.configureMouseEvent(editor, editable);
    /**
     * While dropping over element, it will insert the dropped content
     * before the existing element instead of replacing it.
     * Remove unsupported formatting from the pasted content.
     */
    this.handleEditorContentInsertionProcess(editor);

    /**
     * Register Events required for Image Carousel.
     */
    this.registerImageCarouselEvents();

    // Attach the function to the load event
    window.addEventListener('load', () => EditorUtil.adjustWidthForSmallDevices(editor));
    // Attach the function to the resize event
    window.addEventListener('resize', () => EditorUtil.adjustWidthForSmallDevices(editor));
  }

  /**
   * Detect changes on editor.
   *
   * @param editor editor data.
   * @param unit page data.
   */
  differChanges(editor, unit) {
    editor.model.document.on('change:data', (event, data) => {
      if (data.isTyping || data.isUndoable) {
        const changes = editor.model.document.differ.getChanges({ includeChangesInGraveyard: false });
        for (const entry of changes) {
          if (MEDIA.includes(entry.name) && entry.type === 'remove') {
            const blockId = entry.attributes.get('bulb-block-id');
            const videoType = entry.attributes.get('video-type');
            if (videoType !== 'instantPlay') {
              // Timeout needed to allow cke perform first it's priority task.
              setTimeout(() => {
                this.undoRedoService.handleUndoRedo(unit, blockId, null, 'redo', 'remove');
              });
            }
          }
        }
      }
    });
  }

  /**
   * Configure global keydown event for the editor.
   *
   * @param editor editor data.
   * @param unit page data
   */
  private configureKeydownEvent(editor, unit): void {
    editor.editing.view.document.on('keydown', (event, data) => {
      EditorUtil.setCommentToolbarStatus(editor, false);
      EditorUtil.toggleCommentToolbarBtn(editor);
    }, { priority: 'highest' });
    editor.editing.view.document.on('keyup', (event, data) => {
      EditorUtil.setCommentToolbarStatus(editor, false);
      EditorUtil.toggleCommentToolbarBtn(editor);
    }, { priority: 'highest' });
  }

  /**
   * Configure global keydown event for the editor.
   *
   * @param editor editor data.
   * @param editable check if the page is editable.
   */
  private configureMouseEvent(editor, editable): void {
    /**
     * While dropping over element, it will insert the dropped content
     * before the existing element instead of replacing it.
     * Remove unsupported formatting from the pasted content.
     */
    editor.editing.view.document.on('mousedown', (event, data) => {
      CKEEventService.showCaptionOnWidgetSelection(editor, data);
    }, { priority: 'highest' });

    if (editable) {
      document.addEventListener('mouseup', EditorUtil.handleClickOutsideEditor.bind(editor));
    }
    editor.editing.view.document.on('mouseup', (event, data) => {
      if (editable) {
        EditorUtil.setCommentToolbarStatus(editor, false);
        if (data.domTarget.nodeName !== 'FIGCAPTION' && data.domTarget.nodeName !== 'DIV') {
          EditorUtil.toggleCommentToolbarBtn(editor);
        }
      } else {
        EditorUtil.handleCommentBalloonVisibility(editor);
      }
    }, { priority: 'highest' });
  }

  /**
   * Configure global dragend event for the editor.
   *
   * @param editor
   */
  private static configureEditorDragEvent(editor): void {
    // Register event to handle drag outside of editor.
    editor.editing.view.document.on('dragend', (event) => {
      EditorUtil.updateHeaderVisibility('block');
      editor.plugins.get('WidgetToolbarRepository').clearForceDisabled('dragDrop');
      event.stop();
    }, { priority: 'highest' });

    // Register event to handle dragging event which will hide toolbar and header of the page during drag.
    editor.editing.view.document.on('dragging', (event) => {
      EditorUtil.updateHeaderVisibility('none');
    }, { priority: 'highest' });

    // Avoid replacing the element while dropping, instead inserts the dropped content before.
    editor.editing.view.document.on('drop', (event, data) => {
      CKEditorService.dropTargetElement = data.domTarget;
      EditorUtil.updateHeaderVisibility('block');
    });
  }

  /**
   * Configure global copy and cut  event for the editor.
   *
   * @param editor
   */
  private static handleCopyAndCutEvent(editor): void {
    editor.editing.view.document.on('copy', (event, data) => {
      EditorUtil.restrictCopyCutEvent(editor, event, data);
    }, { priority: 'highest' });

    editor.editing.view.document.on('cut', (event, data) => {
      EditorUtil.restrictCopyCutEvent(editor, event, data);
    }, { priority: 'highest' });
  }

  /**
   * Configure global paste event for the editor.
   *
   * @param editor
   */
  private static handlePasteEvent(editor): void {
    editor.editing.view.document.on('paste', (event, data) => {
      EditorUtil.handlePaste(editor, event, data);
    }, { priority: 'highest' });
  }

  /**
   * Register image carousel events for the editor.
   * @private
   */
  private registerImageCarouselEvents(): void {
    /**
     * Event to handle insert image carousel.
     */
    window.addEventListener(
      'openAssetLibraryCarouselType',
      this.imageCarouselService.handleImageCarouselButtonClick,
      false,
    );
    /**
     * Event to handle adding a slide to the carousel.
     */
    window.addEventListener('addImageToCarousel', this.imageCarouselService.handleAddingSlideToCarousel, false);
    /**
     * Event to handle slide addition in carousel.
     */
    window.addEventListener('imageCarouselAddSlide', this.imageCarouselService.handleImageCarouselSlideAdd, false);
    /**
     * Event to handle removing a slide from the carousel.
     */
    window.addEventListener(
      'removeImageFromCarousel',
      this.imageCarouselService.handleRemovingSlideFromCarousel,
      false,
    );
    /**
     * Event to handle updating a carousel caption.
     */
    window.addEventListener('changeCarouselCaption', this.imageCarouselService.updateCarouselCaption, false);
    /**
     * Event to handle the cropping of an image carousel:
     */
    window.addEventListener('bulbCropCarouselImage', this.imageCarouselService.handleImageCarouselCrop, false);
  }

  /**
   * Handle replace on multiple element selection.
   *
   * @param selection selected element.
   * @private
   */
  private handleMultipleSelectionReplace(selection) {
    const range = selection?.getFirstRange();

    for (const item of range.getItems()) {
      if (item && MEDIA.includes(item.name)) {
        const blockId = item.getAttribute('bulb-block-id');
        this.dispatchUndoRedoEvent(blockId, item.name);
      }
    }
  }

  /**
   * Customize the contentInsertion event of CKE editor.
   * @param editor Current editor instance.
   */
  private handleEditorContentInsertionProcess(editor) {
    editor.plugins.get('ClipboardPipeline').on('contentInsertion', (evt, data) => {
      const { content } = data;
      this.ckEditorService.addParagraphBeforeDroppedBlock(editor, data);
      const selection = editor.model.document.selection.getSelectedElement() || editor.model.document.selection;
      if (data.dataTransfer._native.types.includes('text/plain') && selection.name !== 'table') {
        // Remove unsupported formatting from the pasted content.
        data.content = EditorUtil.cleanedUpUnwantedTags(content);
        // Handle undo/redo for media blocks when user paste text over any media block.
        const blockId = selection.getAttribute('bulb-block-id');
        if (blockId) {
          this.dispatchUndoRedoEvent(blockId, selection.name);
        } else {
          // When user select multiple items and paste any text then this else will handle undo/redo.
          this.handleMultipleSelectionReplace(selection);
        }
      }
    }, { priority: 'highest' });
  }

  /**
   * Dispatch undo/redo event for the block.
   * @param blockId Id of the selected block.
   * @param blockName Name of the selected block.
   */
  private dispatchUndoRedoEvent(blockId, blockName) {
    const undoRedoEvent = new CustomEvent('blockElementUnload', {
      detail: {
        blockId,
        blockName,
      },
    });
    window.dispatchEvent(undoRedoEvent);
  }

  /**
   * Show caption when widget is selected.
   * @param editor Current editor instance.
   * @param data Event Data.
   */
  static showCaptionOnWidgetSelection(editor, data) {
    Array.from(document.getElementsByClassName('image-carousel')).forEach((element) => {
      if (element.classList.contains('ck-widget_selected')) {
        Array.from(element.getElementsByTagName('figcaption')).forEach((element) => {
          if (element.classList.contains('ck-placeholder')) {
            (element as HTMLElement).classList.add('ck-hidden');
          }
        });
      }
    });

    const selectedWidget = data.domTarget.closest('figure');
    if (selectedWidget) {
      Array.from(selectedWidget.getElementsByTagName('figcaption')).forEach((element) => {
        // Make the caption visible on selection.
        (element as HTMLElement).classList.remove('ck-hidden');
      });
    }
  }
}
